import { Transaction } from '../../types';
import { ProgressStep, ProgressState, StepStatus } from './types';

export const PROGRESS_STEPS: ProgressStep[] = [
    {
        id: 'payment_received',
        label: 'Payment Received',
        shortLabel: 'Paid',
        icon: '💳', // Changed from 💰
        description: 'Funds are securely held in escrow',
        statuses: ['pending', 'in_escrow'],
        status: 'pending'
    },
    {
        id: 'tracking_provided',
        label: 'Tracking Details',
        shortLabel: 'Shipped',
        icon: '📦', // Changed from 🚚
        description: 'Shipping information provided by seller',
        statuses: ['in_escrow'], // with trackingId
        status: 'pending'
    },
    {
        id: 'payment_released',
        label: 'Payment Released',
        shortLabel: 'Released',
        icon: '💸', // Changed from ✅
        description: 'Funds released from escrow to seller',
        statuses: ['released', 'funds_withdrawal_in_progress'],
        status: 'pending'
    },
    {
        id: 'complete',
        label: 'Complete',
        shortLabel: 'Done',
        icon: '✨', // Changed from 🎉
        description: 'Transaction completed successfully',
        statuses: ['funds_withdrawn', 'transaction_completed'],
        status: 'pending'
    }
];

/**
 * Determines the current step based on transaction status and tracking information
 */
function getCurrentStep(transaction: Transaction): number {
  // Handle edge cases first
  if (!transaction) return 1;
  
  // Step 4: Complete - Transaction fully finished
  // Note: These statuses may come from backend but not defined in frontend interface yet
  if ((transaction.status as string) === 'funds_withdrawn' ||
      (transaction.status as string) === 'transaction_completed') {
    return 4;
  }
  
  // Step 3: Payment Released - Funds released from escrow
  if (transaction.status === 'released' ||
      (transaction.status as string) === 'funds_withdrawal_in_progress') {
    return 3;
  }
  
  // Step 2: Tracking Details Provided - In escrow with tracking
  if (transaction.status === 'in_escrow' && transaction.trackingId) {
    return 2;
  }
  
  // Step 1: Payment Received - In escrow without tracking
  if (transaction.status === 'in_escrow' ||
      transaction.status === 'pending') {
    return 1;
  }
  
  // Handle other statuses (disputed, cancelled, etc.)
  // These don't fit the normal flow but we show step 1 as baseline
  return 1;
}

/**
 * Determines the status of a specific step based on current progress
 */
function getStepStatus(stepNumber: number, currentStep: number): StepStatus {
  if (stepNumber < currentStep) return 'completed';
  if (stepNumber === currentStep) return 'current';
  return 'pending';
}

/**
 * Handles special cases for disputed transactions
 */
function handleDisputedTransaction(transaction: Transaction): Partial<ProgressState> {
  // For disputed transactions, show progress up to the point of dispute
  // but add visual indicator that transaction is disputed
  const baseStep = getCurrentStep({
    ...transaction,
    status: 'in_escrow' as any // Show as if still in escrow
  });
  
  return {
    isDisputed: true,
    currentStep: baseStep
  };
}

/**
 * Handles special cases for cancelled/refunded transactions
 */
function handleCancelledTransaction(transaction: Transaction): Partial<ProgressState> {
  // Show minimal progress for cancelled transactions
  return {
    currentStep: 1,
    completedSteps: 0,
    progressPercentage: 0,
    isCancelled: true
  };
}

/**
 * Main function to calculate the complete progress state for a transaction
 */
export function calculateProgressState(transaction: Transaction): ProgressState {
  if (!transaction) {
    throw new Error('Transaction is required to calculate progress state');
  }

  // Handle special cases first
  let specialState: Partial<ProgressState> = {};
  
  if (transaction.status === 'disputed' || transaction.isDisputed) {
    specialState = handleDisputedTransaction(transaction);
  } else if (transaction.status === 'cancelled' || transaction.status === 'refunded') {
    specialState = handleCancelledTransaction(transaction);
  }

  // Calculate normal progress
  const currentStep = specialState.currentStep || getCurrentStep(transaction);
  const completedSteps = specialState.completedSteps !== undefined 
    ? specialState.completedSteps 
    : Math.max(0, currentStep - 1);
  const progressPercentage = specialState.progressPercentage !== undefined
    ? specialState.progressPercentage
    : (completedSteps / (PROGRESS_STEPS.length - 1)) * 100;
  
  // Create steps with proper status
  const steps = PROGRESS_STEPS.map((step, index) => ({
    ...step,
    status: specialState.isCancelled && index > 0 
      ? 'skipped' as StepStatus
      : getStepStatus(index + 1, currentStep)
  }));
  
  return {
    currentStep,
    steps,
    completedSteps,
    progressPercentage,
    isDisputed: specialState.isDisputed || false,
    isCancelled: specialState.isCancelled || false,
    hasError: false,
    ...specialState
  };
}

/**
 * Validates if a transaction should show the progress indicator
 */
export function shouldShowProgressIndicator(transaction: Transaction): boolean {
  if (!transaction) return false;
  
  const relevantStatuses = [
    'pending',
    'in_escrow',
    'released',
    'funds_withdrawal_in_progress',
    'funds_withdrawn',
    'transaction_completed',
    'disputed' // Show for disputed transactions too
  ];
  
  return relevantStatuses.includes(transaction.status as string);
}

/**
 * Gets appropriate size based on screen context
 */
export function getContextualSize(screenContext: 'summary' | 'release' | 'default'): 'small' | 'medium' | 'large' {
  switch (screenContext) {
    case 'summary': return 'medium'; // Detailed view
    case 'release': return 'small';  // Compact context view
    default: return 'medium';
  }
}

/**
 * Generates accessible label for the progress indicator
 */
export function getProgressLabel(progressState: ProgressState): string {
  const { currentStep, steps } = progressState;
  const currentStepLabel = steps[currentStep - 1]?.label || 'Unknown';
  return `Transaction progress: ${currentStepLabel}, step ${currentStep} of ${steps.length}`;
}

/**
 * Generates accessible label for individual steps
 */
export function getStepAriaLabel(step: ProgressStep, index: number): string {
  const stepNumber = index + 1;
  const statusText = {
    completed: 'completed',
    current: 'current, in progress',
    pending: 'pending, not started',
    skipped: 'skipped',
    error: 'error occurred'
  }[step.status];
  
  return `Step ${stepNumber}: ${step.label}, ${statusText}. ${step.description}`;
}

/**
 * Utility for announcing progress changes to screen readers
 */
export function announceProgressChange(currentStep: number, totalSteps: number, stepLabel: string): void {
  const announcement = `Progress updated: ${stepLabel}, step ${currentStep} of ${totalSteps}`;
  
  // Create live region for announcements
  let liveRegion = document.getElementById('progress-announcements');
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'progress-announcements';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    document.body.appendChild(liveRegion);
  }
  
  liveRegion.textContent = announcement;
}